<template>
  <div class="key-issues-view">
    <div class="section-header">
      <h4>Key Issues</h4>
    </div>

    <div class="key-issues-tabs">
      <button v-for="tab in tabs" :key="tab.key"
              @click="activeTab = tab.key"
              :class="['tab-btn', { active: activeTab === tab.key }]">
        {{ tab.label }}
      </button>
    </div>

    <div class="tab-content">
      <div v-show="activeTab === 'revenue'" class="tab-panel">
        <h5>Revenue Impact</h5>
        <div v-if="keyIssues?.revenue_rows?.length" class="impact-table">
          <div class="table-header">
            <div class="particulars-col">Particulars</div>
            <div v-for="year in getYearRange()" :key="year" class="year-col">{{ year }}</div>
          </div>
          <div v-for="(row, index) in keyIssues.revenue_rows" :key="index" class="table-row">
            <div class="particulars-col">{{ row.particulars || '-' }}</div>
            <div v-for="year in getYearRange()" :key="year" class="year-col">
              {{ formatCurrency(row[year]) }}
            </div>
          </div>
          <div class="table-row total-row">
            <div class="particulars-col">TOTAL</div>
            <div v-for="year in getYearRange()" :key="year" class="year-col">
              {{ getYearTotal(keyIssues.revenue_rows, year) }}
            </div>
          </div>
        </div>
        <div v-else class="no-data">No revenue impact data available</div>
      </div>

      <div v-show="activeTab === 'budgetary'" class="tab-panel">
        <h5>Budgetary Impact</h5>
        <div v-if="keyIssues?.budgetary_rows?.length" class="impact-table">
          <div class="table-header">
            <div class="particulars-col">Particulars</div>
            <div v-for="year in getYearRange()" :key="year" class="year-col">{{ year }}</div>
          </div>
          <div v-for="(row, index) in keyIssues.budgetary_rows" :key="index" class="table-row">
            <div class="particulars-col">{{ row.particulars || '-' }}</div>
            <div v-for="year in getYearRange()" :key="year" class="year-col">
              {{ formatCurrency(row[year]) }}
            </div>
          </div>
          <div class="table-row total-row">
            <div class="particulars-col">TOTAL</div>
            <div v-for="year in getYearRange()" :key="year" class="year-col">
              {{ getYearTotal(keyIssues.budgetary_rows, year) }}
            </div>
          </div>
        </div>
        <div v-else class="no-data">No budgetary impact data available</div>
      </div>

      <div v-show="activeTab === 'dof_concerns'" class="tab-panel">
        <h5>DOF Concerns</h5>
        <div v-if="keyIssues?.dof_concerns_rows?.length" class="concerns-list">
          <div v-for="(concern, index) in keyIssues.dof_concerns_rows" :key="index" class="concern-item">
            <div class="concern-header">
              <span>Concern #{{ index + 1 }}</span>
            </div>
            <div class="concern-content">
              {{ concern.concerns || '-' }}
            </div>
          </div>
        </div>
        <div v-else class="no-data">No DOF concerns available</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  keyIssues: {
    type: Object,
    default: () => ({})
  }
})

const activeTab = ref('revenue')

const tabs = [
  { key: 'revenue', label: 'Revenue Impact' },
  { key: 'budgetary', label: 'Budgetary Impact' },
  { key: 'dof_concerns', label: 'DOF Concerns' }
]

const getMaxYearFromData = (rows) => {
  if (!rows || !Array.isArray(rows) || rows.length === 0) return new Date().getFullYear()
  let maxYear = new Date().getFullYear()
  rows.forEach(row => {
    Object.keys(row).forEach(key => {
      const year = parseInt(key)
      if (!isNaN(year) && year > maxYear) {
        maxYear = year
      }
    })
  })
  return maxYear
}

const getYearRange = () => {
  const currentYear = new Date().getFullYear()
  const revenueMaxYear = getMaxYearFromData(props.keyIssues?.revenue_rows)
  const budgetaryMaxYear = getMaxYearFromData(props.keyIssues?.budgetary_rows)
  const maxYear = Math.max(revenueMaxYear, budgetaryMaxYear, currentYear + 5)
  return Array.from({ length: maxYear - currentYear + 1 }, (_, i) => currentYear + i)
}

const formatCurrency = (value) => {
  if (!value || value === 0) return '-'
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2
  }).format(value)
}

const getYearTotal = (rows, year) => {
  if (!rows || !Array.isArray(rows)) return '₱0.00'
  const total = rows.reduce((sum, row) => {
    const value = parseFloat(row[year]) || 0
    return sum + value
  }, 0)
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2
  }).format(total)
}
</script>

<style scoped>
.key-issues-view {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid #e5e7eb;
  margin-top: 2rem;
}

.section-header h4 {
  color: #1e3a8a;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #fbbf24;
}

.key-issues-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.tab-btn {
  padding: 0.75rem 1.5rem;
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: #f3f4f6;
  border-color: #1e3a8a;
}

.tab-btn.active {
  background: #1e3a8a;
  color: white;
  border-color: #1e3a8a;
}

.tab-panel h5 {
  color: #1e3a8a;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
}

.impact-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: auto;
}

.table-header {
  display: flex;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  min-width: 800px;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  min-width: 800px;
}

.table-row:last-child {
  border-bottom: none;
}

.particulars-col, .year-col {
  padding: 0.75rem;
  font-size: 0.9rem;
  white-space: nowrap;
}

.particulars-col {
  font-weight: 600;
  color: #374151;
  border-right: 1px solid #e5e7eb;
  flex: 2;
  min-width: 200px;
}

.year-col {
  text-align: center;
  color: #6b7280;
  border-right: 1px solid #e5e7eb;
  flex: 1;
  min-width: 100px;
}

.year-col:last-child {
  border-right: none;
}

.table-header .particulars-col,
.table-header .year-col {
  font-weight: 700;
  color: #1e3a8a;
  background: #f8fafc;
}

.total-row {
  background: #fef3c7;
  font-weight: 700;
}

.total-row .particulars-col,
.total-row .year-col {
  color: #92400e;
  font-weight: 700;
}

.concerns-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.concern-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
}

.concern-header {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.concern-content {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.5;
}

.no-data {
  text-align: center;
  color: #6b7280;
  font-style: italic;
  padding: 2rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

@media (max-width: 768px) {
  .key-issues-tabs {
    flex-direction: column;
  }
  
  .impact-table {
    overflow-x: auto;
  }
  
  .table-header,
  .table-row {
    min-width: 600px;
  }
}
</style>